import { createMemo, createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Gift, Loader2, PlayCircle } from "lucide-solid";
import type { ShopProduct } from "@famidex/powersync";

import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { createPowerSyncQuery } from "@famidex/shared/solid";
import {
  computeBalanceFromLedger,
  DEFAULT_MONETIZATION,
  getAcornTransactionsQuery,
  getDailyRewardClaimsQuery,
  getRedemptionsQuery,
  getShopProductsQuery,
  getWalletQuery,
  queryKeys,
  rewardVideosRemaining,
} from "@famidex/shared";
import {
  claimDailyReward,
  purchasePack,
  redeemReward,
  watchRewardedVideo,
} from "../data/economy-rest";
import { AcornGlyph, formatAcorns } from "./shop-visuals";
import { CoinShop } from "./coin-shop";
import { RedemptionShop } from "./redemption-shop";

type Flash = { tone: "success" | "error"; message: string };

// oxlint-disable-next-line no-magic-numbers -- duración del flash de feedback (ms)
const FLASH_DURATION_MS = 3200;

/** Clave de día natural (YYYY-MM-DD) de una fecha ISO, sin literales mágicos. */
function dateKey(iso: string): string {
  return iso.split("T")[0] ?? iso;
}

function todayKey(): string {
  return dateKey(new Date().toISOString());
}

/**
 * 🌰 Mercado de Bellotas (tab /market). Economía de moneda blanda: saldo
 * SERVER-AUTHORITATIVE leído por sync (`my_wallet`), tienda por sync
 * (`shop_catalog`); toda mutación por REST (`/api/economy/*`) — el cliente jamás
 * fija su saldo. Reúne cinco pantallas Stitch: saldo + recompensa diaria + vídeo
 * recompensado + packs (tienda de monedas) + canje de marcos/stickers.
 */
export function MarketPage() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();
  const { userId } = session;

  const [busy, setBusy] = createSignal(false);
  const [flash, setFlash] = createSignal<Flash | null>(null);
  let flashTimer: ReturnType<typeof setTimeout> | undefined = undefined;

  const showFlash = (f: Flash) => {
    setFlash(f);
    if (flashTimer) {
      clearTimeout(flashTimer);
    }
    flashTimer = setTimeout(() => setFlash(null), FLASH_DURATION_MS);
  };

  const enabled = () => Boolean(drizzle() && userId());

  const walletRow = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.economy.wallet(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getWalletQuery(d, uid) : null;
    },
  });

  const transactions = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.economy.transactions(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getAcornTransactionsQuery(d, uid) : null;
    },
  });

  const shop = createPowerSyncQuery({
    db,
    enabled: () => Boolean(drizzle()),
    queryKey: () => queryKeys.economy.shop(),
    queryFn: () => {
      const d = drizzle();
      return d ? getShopProductsQuery(d) : null;
    },
  });

  const dailyClaims = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.economy.dailyClaims(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getDailyRewardClaimsQuery(d, uid) : null;
    },
  });

  const redemptions = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.economy.redemptions(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getRedemptionsQuery(d, uid) : null;
    },
  });

  // Saldo: la fila autoritativa del wallet; si aún no ha sincronizado, se proyecta
  // del ledger (misma verdad, función pura). El servidor sigue siendo la autoridad.
  const balance = createMemo(() => {
    const [row] = walletRow.data ?? [];
    if (row && typeof row.balance_acorns === "number") {
      return row.balance_acorns;
    }
    return computeBalanceFromLedger(transactions.data ?? []);
  });

  const videosSeenToday = createMemo(() => {
    const today = todayKey();
    return (transactions.data ?? []).filter(
      (row) => row.reason === "reward_video" && dateKey(row.created_at ?? "") === today,
    ).length;
  });
  const videosLeft = createMemo(() => rewardVideosRemaining(videosSeenToday()));
  const videosLeftLabel = createMemo(() => {
    const left = videosLeft();
    return t(msg`Mira un vídeo corto. Quedan ${left} hoy.`);
  });

  const claimedToday = createMemo(() => {
    const today = todayKey();
    return (dailyClaims.data ?? []).some((c) => dateKey(c.claimed_on ?? "") === today);
  });

  const redeemedIds = createMemo(
    () => new Set((redemptions.data ?? []).map((r) => r.shop_product_id ?? "")),
  );

  const packs = createMemo(() => (shop.data ?? []).filter((p) => p.kind === "pack"));
  const frames = createMemo(() => (shop.data ?? []).filter((p) => p.kind === "frame"));
  const stickers = createMemo(() => (shop.data ?? []).filter((p) => p.kind === "sticker"));

  const errorMessage = (err: unknown): string =>
    err instanceof Error && err.message ? err.message : t(msg`Algo salió mal. Inténtalo de nuevo.`);

  const runAction = async (action: () => Promise<Flash>) => {
    if (busy()) {
      return;
    }
    setBusy(true);
    try {
      showFlash(await action());
    } catch (err) {
      showFlash({ tone: "error", message: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const handleClaimDaily = () =>
    void runAction(async () => {
      const res = await claimDailyReward();
      const gained = res.transaction.delta;
      return { tone: "success", message: t(msg`¡+${gained} bellotas por hoy!`) };
    });

  const handleWatchVideo = () =>
    void runAction(async () => {
      // Red de anuncios real detrás de flag; con el stub la recompensa la acredita
      // igualmente el servidor (sin muro). Ver economy/flags.ts.
      const res = await watchRewardedVideo();
      const gained = res.transaction.delta;
      return { tone: "success", message: t(msg`¡+${gained} bellotas!`) };
    });

  const handlePurchase = (product: ShopProduct) =>
    void runAction(async () => {
      const res = await purchasePack(product.id);
      const gained = res.transaction.delta;
      return { tone: "success", message: t(msg`¡+${gained} bellotas añadidas!`) };
    });

  const handleRedeem = (product: ShopProduct) =>
    void runAction(async () => {
      await redeemReward(product.id);
      const name = product.name ?? "";
      return { tone: "success", message: t(msg`¡Canjeado: ${name}!`) };
    });

  return (
    <div class="flex flex-col gap-6" data-testid="market-page">
      {/* Saldo */}
      <header class="rounded-2xl border border-border/60 bg-card/70 p-5 text-center">
        <p class="text-xs font-bold uppercase tracking-widest text-primary">
          {t(msg`Tus Bellotas`)}
        </p>
        <div
          class="mt-1 flex items-center justify-center gap-2 text-4xl font-bold text-foreground"
          data-testid="acorn-balance"
        >
          <AcornGlyph class="h-8 w-8 text-primary" />
          {formatAcorns(balance())}
        </div>
        <p class="mt-2 text-xs text-muted-foreground">
          {t(msg`Gana bellotas y canjéalas por marcos y stickers de coleccionista.`)}
        </p>
      </header>

      {/* Flash de feedback */}
      <Show when={flash()}>
        {(f) => (
          <div
            data-testid="economy-flash"
            class="rounded-xl px-4 py-3 text-center text-sm font-semibold"
            classList={{
              "bg-primary/15 text-primary": f().tone === "success",
              "bg-destructive/15 text-destructive": f().tone === "error",
            }}
          >
            {f().message}
          </div>
        )}
      </Show>

      {/* Recompensa diaria */}
      <section
        class="flex items-center gap-3 rounded-2xl border border-border/60 bg-accent/40 p-4"
        data-testid="daily-reward"
      >
        <span class="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground">
          <Gift class="h-6 w-6" />
        </span>
        <div class="min-w-0 flex-1">
          <h2 class="text-sm font-semibold text-foreground">{t(msg`Recompensa diaria`)}</h2>
          <p class="text-xs text-muted-foreground">
            {t(msg`Vuelve cada día por bellotas gratis.`)}
          </p>
        </div>
        <button
          type="button"
          data-testid="claim-daily"
          disabled={busy() || claimedToday()}
          onClick={handleClaimDaily}
          class="shrink-0 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground transition-transform active:scale-95 disabled:opacity-60"
        >
          <Show when={!claimedToday()} fallback={t(msg`Reclamada`)}>
            {t(msg`Reclamar`)}
          </Show>
        </button>
      </section>

      {/* Vídeo recompensado */}
      <section
        class="flex items-center gap-3 rounded-2xl border border-border/60 bg-card p-4"
        data-testid="earn-acorns"
      >
        <span class="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accent text-accent-foreground">
          <PlayCircle class="h-6 w-6" />
        </span>
        <div class="min-w-0 flex-1">
          <h2 class="text-sm font-semibold text-foreground">{t(msg`Gana bellotas gratis`)}</h2>
          <p class="text-xs text-muted-foreground">{videosLeftLabel()}</p>
        </div>
        <button
          type="button"
          data-testid="watch-video"
          disabled={busy() || videosLeft() <= 0}
          onClick={handleWatchVideo}
          class="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground transition-transform active:scale-95 disabled:opacity-60"
        >
          <Show when={busy()}>
            <Loader2 class="h-3.5 w-3.5 animate-spin" />
          </Show>
          <AcornGlyph class="h-3.5 w-3.5" />
          +10
        </button>
      </section>

      {/* Packs (tienda de monedas) */}
      <Show when={packs().length > 0}>
        <CoinShop
          packs={packs()}
          iapEnabled={DEFAULT_MONETIZATION.iapEnabled}
          busy={busy()}
          onPurchase={handlePurchase}
        />
      </Show>

      {/* Canje de recompensas (marcos) */}
      <RedemptionShop
        title={t(msg`Canje de recompensas`)}
        testid="reward-redemption"
        items={frames()}
        balance={balance()}
        redeemedIds={redeemedIds()}
        busy={busy()}
        onRedeem={handleRedeem}
      />

      {/* Tienda de stickers */}
      <RedemptionShop
        title={t(msg`Tienda de stickers`)}
        testid="sticker-shop"
        items={stickers()}
        balance={balance()}
        redeemedIds={redeemedIds()}
        busy={busy()}
        onRedeem={handleRedeem}
      />
    </div>
  );
}
